import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { authHeaders, formatGuideLongDate, guidesFetch } from './guidesUtils';
import GuideItemIcon, { guideItemKind } from './GuideItemIcon';

function typeLabel(type) {
  if (type === 'notebook') return 'notebook';
  if (type === 'article') return 'article';
  if (type === 'flashcard_deck') return 'deck';
  if (type === 'flashcard_set') return 'set';
  return 'guide';
}

function authorName(creator) {
  if (!creator) return 'Unknown';
  return `${creator.firstName || ''} ${creator.lastName || ''}`.trim() || 'Unknown';
}

function countParts(item) {
  const kind = guideItemKind(item);
  const parts = [];
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  if (kind === 'notebook') {
    if (item.sectionCount != null) parts.push(plural(item.sectionCount, 'section', 'sections'));
    if (item.topicCount != null) parts.push(plural(item.topicCount, 'topic', 'topics'));
  } else if (kind === 'deck') {
    if (item.sectionCount != null) parts.push(plural(item.sectionCount, 'section', 'sections'));
    if (item.setCount != null) parts.push(plural(item.setCount, 'set', 'sets'));
  } else if (kind === 'set' && item.cardCount != null) {
    parts.push(plural(item.cardCount, 'card', 'cards'));
  }
  return parts;
}

export default function DiscoverItemModal({ show, onHide, targetType, targetId, onOpen }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!show || !targetType || !targetId) return;
    setLoading(true);
    setItem(null);
    guidesFetch(`/discover/${targetType}/${targetId}`, { headers: authHeaders() })
      .then(setItem)
      .catch((err) => {
        toast.error(err.message || 'Failed to load details');
        onHide();
      })
      .finally(() => setLoading(false));
  }, [show, targetType, targetId, onHide]);

  const addToCollection = async () => {
    if (!item) return;
    setAdding(true);
    try {
      await guidesFetch('/collections', {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ targetType: item.type, targetId: item.id })
      });
      toast.success(item.allowEdits ? 'Added to collection (editable copy created)' : 'Added to collection');
      setItem((prev) => (prev ? { ...prev, inCollection: true } : prev));
    } catch (err) {
      toast.error(err.message || 'Failed to add to collection');
    } finally {
      setAdding(false);
    }
  };

  const counts = item ? countParts(item) : [];
  const published = item ? formatGuideLongDate(item.publishedAt || item.createdAt) : '';
  const updated = item ? formatGuideLongDate(item.updatedAt) : '';
  const kind = item ? guideItemKind(item) : 'notebook';

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="guides-discover-modal"
      contentClassName="guides-discover-modal-content"
    >
      {loading || !item ? (
        <div className="guides-loading"><Spinner /></div>
      ) : (
        <div className="guides-discover-card">
          <div className="guides-discover-head">
            <span className={`guides-item-icon guides-item-icon-${kind}`}>
              <GuideItemIcon item={item} />
            </span>
            <div className="guides-discover-heading">
              <h5 className="guides-discover-title">{item.title}</h5>
              <div className="guides-discover-sub">
                <span>{authorName(item.creator)}</span>
                {published ? <span>{published}</span> : null}
                {counts.map((part) => (
                  <span key={part}>{part}</span>
                ))}
              </div>
            </div>
          </div>

          <p className="guides-discover-desc">
            {item.description || 'No description provided.'}
          </p>

          <div className="guides-discover-facts">
            <span>
              <span className="guides-discover-fact-label">Category:</span>
              {' '}
              {item.category || '—'}
            </span>
            <span>
              <span className="guides-discover-fact-label">Last Updated:</span>
              {' '}
              {updated || '—'}
            </span>
          </div>

          <div className="guides-discover-actions">
            <span className={`guides-discover-type guides-discover-type-${kind}`}>
              {typeLabel(item.type)}
            </span>
            <div className="guides-discover-buttons">
              <Button variant="outline-secondary" size="sm" onClick={onHide}>
                Close
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  onOpen?.(item);
                  onHide();
                }}
              >
                Open
              </Button>
              {item.inCollection ? (
                <Button variant="outline-secondary" size="sm" disabled>
                  In collection
                </Button>
              ) : (
                <Button variant="primary" size="sm" onClick={addToCollection} disabled={adding}>
                  {adding ? <Spinner size="sm" /> : 'Add to collection'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
